import {
  Bot,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Sparkles,
  Headphones,
  LifeBuoy,
  CalendarDays,
  Phone,
  Mail,
  Zap,
  Heart,
} from "lucide-react";

// Launcher icons come from Lucide so the picker can offer the whole set rather
// than four hardcoded shapes.
//
// A chosen icon is stored as "lucide:MessageCircle" in `launcher.style`. The
// prefix keeps it distinguishable from the legacy preset ids ("bubble",
// "smile", "spark", "logo") that existing assistants may still carry, so old
// records keep rendering.
//
// Only the suggested handful is imported statically. Pulling in Lucide's
// `icons` map instead references all ~1,700 components and added ~139 kB gzip
// to the main bundle, so the full set is loaded on demand — see loadIconMap().

export const LUCIDE_PREFIX = "lucide:";

const SUGGESTED_MAP = {
  Bot,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Sparkles,
  Headphones,
  LifeBuoy,
  CalendarDays,
  Phone,
  Mail,
  Zap,
  Heart,
};

export const SUGGESTED_ICONS = Object.keys(SUGGESTED_MAP);

export const isLucideIcon = (value) =>
  typeof value === "string" && value.startsWith(LUCIDE_PREFIX);

export const lucideNameOf = (value) =>
  isLucideIcon(value) ? value.slice(LUCIDE_PREFIX.length) : "";

export const toStoredValue = (name) => `${LUCIDE_PREFIX}${name}`;

/** Synchronous lookup — only the suggested icons are loaded up front. */
export const suggestedComponentOf = (value) => {
  const name = lucideNameOf(value);
  return name ? SUGGESTED_MAP[name] || null : null;
};

// The whole library, fetched as its own chunk the first time it's needed and
// cached after that.
let iconMapPromise = null;

export const loadIconMap = () => {
  if (!iconMapPromise) {
    iconMapPromise = import("lucide-react").then((module) => module.icons);
  }
  return iconMapPromise;
};

/**
 * Turns "MessageCircle" into "Message Circle" for tooltips and search, so a
 * search for "message circle" matches too.
 */
export const humanizeIconName = (name) =>
  name.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

export const searchIcons = (iconMap, query, limit = 120) => {
  const term = query.trim().toLowerCase();

  if (!term) return SUGGESTED_ICONS;
  if (!iconMap) return [];

  const matches = [];

  for (const name of Object.keys(iconMap)) {
    if (
      name.toLowerCase().includes(term) ||
      humanizeIconName(name).toLowerCase().includes(term)
    ) {
      matches.push(name);
      if (matches.length >= limit) break;
    }
  }

  return matches;
};
